// -------------------------------
// 🔹 Imports externes (librairies tierces)
// -------------------------------
import { useMutation } from '@tanstack/vue-query';
import type { AxiosError } from 'axios';

// -------------------------------
// 🔹 Imports internes — Références réactives (refs globales)
// -------------------------------
import { isGlobalError, GlobalErrorMessage } from '@/refs/common.refs';
import { isLoaderLoginFormActivated } from '@/refs/login.form.refs';

// -------------------------------
// 🔹 Imports internes — Stores Pinia
// -------------------------------
import { useProductStore } from '@/store/product.store';

// -------------------------------
// 🔹 Imports internes — Types TypeScript
// -------------------------------
import type {
  IPoductBody,
  IProductImage,
  IProductRecommendationRemoveBody,
  IProductRelationBody,
  IUpdateProductMutationPayload,
  IBodySetVisibility,
  IBodyAddAddonGroup,
  IBodyUpdateAddonGroup,
  IBodyAddAddonOption,
} from '@/types/product.types';

/**
 * Hook pour gérer toutes les mutations liées aux produits.
 * Fournit des fonctions pour créer, mettre à jour, supprimer un produit,
 * gérer les images et les recommandations.
 */
export const ProductMutation = () => {
  const productStore = useProductStore();

  // -------------------------------
  // 🔹 Gestion centralisée des erreurs
  // -------------------------------
  const handleError = (err: any) => {
    const error = err as AxiosError;
    const response = error.response?.data as any;
    const message = response?.message?.[0] || 'Une erreur est survenue';

    isGlobalError.value = true;
    GlobalErrorMessage.value = message;
    setTimeout(() => {
      isGlobalError.value = false;
      isLoaderLoginFormActivated.value = false;
    }, 1000);
  };

  // -------------------------------
  // 🔹 Création d'un produit
  // -------------------------------
  const createProduct = useMutation<any, Error, IPoductBody>({
    mutationFn: (payload) => productStore.createProduct(payload),
    onError: handleError,
  });

  // -------------------------------
  // 🔹 Mise à jour d'un produit
  // -------------------------------
  const updateProduct = useMutation<any, Error, IUpdateProductMutationPayload>({
    mutationFn: ({ payload, productId }) =>
      productStore.updateProduct(productId, payload),
    onError: handleError,
  });

  // -------------------------------
  // 🔹 Suppression d'un produit
  // -------------------------------
  const deleteProduct = useMutation<any, Error, string>({
    mutationFn: (productId) => productStore.deleteProduct(productId),
    onSuccess: () => {
      isLoaderLoginFormActivated.value = true;
    },
    onError: handleError,
  });

  // -------------------------------
  // 🔹 Upload d'image pour un produit
  // -------------------------------
  const uploadImage = useMutation<any, Error, IProductImage>({
    mutationFn: (payload) => productStore.uploadImage(payload),
    onError: handleError,
  });

  // -------------------------------
  // 🔹 Création d'une recommandation pour un produit
  // -------------------------------
  const createRecommendation = useMutation<any, Error, IProductRelationBody>({
    mutationFn: (payload) => productStore.createRecommendation(payload),
    onSuccess: () => {
      isLoaderLoginFormActivated.value = true;
    },
    onError: handleError,
  });

  // -------------------------------
  // 🔹 Suppression d'une recommandation pour un produit
  // -------------------------------
  const deleteRecommendation = useMutation<
    any,
    Error,
    IProductRecommendationRemoveBody
  >({
    mutationFn: (payload) => productStore.deleteRecommendation(payload),
    onSuccess: () => {
      isLoaderLoginFormActivated.value = true;
      setTimeout(() => {
        isLoaderLoginFormActivated.value = false;
      }, 1000);
    },
    onError: handleError,
  });

  // ─── Visibilité ───────────────────────────────────────────────
  const setVisibility = useMutation<
    any,
    Error,
    { productId: string; data: IBodySetVisibility }
  >({
    mutationFn: ({ productId, data }) =>
      productStore.setVisibility(productId, data),
    onError: handleError,
  });

  // ─── AddonGroups ──────────────────────────────────────────────
  const addAddonGroup = useMutation<
    any,
    Error,
    { productId: string; data: IBodyAddAddonGroup }
  >({
    mutationFn: ({ productId, data }) =>
      productStore.addAddonGroup(productId, data),
    onError: handleError,
  });

  const updateAddonGroup = useMutation<any, Error, IBodyUpdateAddonGroup>({
    mutationFn: (data) => productStore.updateAddonGroup(data),
    onError: handleError,
  });

  const deleteAddonGroup = useMutation<any, Error>({
    mutationFn: () => productStore.deleteAddonGroup(),
    onError: handleError,
  });

  // ─── Addons (options) ─────────────────────────────────────────
  const addAddonOption = useMutation<any, Error, IBodyAddAddonOption>({
    mutationFn: (data) => productStore.addAddonOption(data),
    onError: handleError,
  });

  const updateAddon = useMutation<any, Error, Record<string, any>>({
    mutationFn: (data) => productStore.updateAddon(data),
    onError: handleError,
  });

  const deleteAddon = useMutation<any, Error>({
    mutationFn: () => productStore.deleteAddon(),
    onError: handleError,
  });

  // -------------------------------
  // 🔹 Fonctions exposées pour les composants
  // -------------------------------
  return {
    handleCreateProduct: (item: IPoductBody) => createProduct.mutateAsync(item),
    handleUpdateProduct: (item: IUpdateProductMutationPayload) =>
      updateProduct.mutateAsync(item),
    handleDeleteProduct: (item: string) => deleteProduct.mutateAsync(item),
    handleUploadImage: (item: IProductImage) => uploadImage.mutateAsync(item),
    handleCreateRecommendation: (item: IProductRelationBody) =>
      createRecommendation.mutateAsync(item),
    handleDeleteRecommendation: (item: IProductRecommendationRemoveBody) =>
      deleteRecommendation.mutate(item),
    handleSetVisibility: (productId: string, data: IBodySetVisibility) =>
      setVisibility.mutateAsync({ productId, data }),
    handleAddAddonGroup: (productId: string, data: IBodyAddAddonGroup) =>
      addAddonGroup.mutateAsync({ productId, data }),
    handleUpdateAddonGroup: (data: IBodyUpdateAddonGroup) =>
      updateAddonGroup.mutateAsync(data),
    handleDeleteAddonGroup: () => deleteAddonGroup.mutateAsync(),
    handleAddAddonOption: (data: IBodyAddAddonOption) =>
      addAddonOption.mutateAsync(data),
    handleUpdateAddon: (data: Record<string, any>) =>
      updateAddon.mutateAsync(data),
    handleDeleteAddon: () => deleteAddon.mutateAsync(),
  };
};
